import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import { LoadingSpinner } from './ui/loading-spinner';
import { useWorkspace } from '../context/workspace-context';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
  type NotificationSettingsUpdate,
} from '../hooks/use-notification';
import { NOTIFICATION_TYPE_SETTINGS } from '../constants/notification-types';
import { useToast } from './ui/toast';
import SendNotificationForm from './send-notification-form';

export default function NotificationSettings() {
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();

  if (isLoadingWorkspaces) {
    return <LoadingSpinner />;
  }

  if (!currentWorkspace) {
    return <p className="text-sm text-muted-foreground">No workspace found</p>;
  }

  return <NotificationSettingsContent organizationId={currentWorkspace.id} />;
}

function NotificationSettingsContent({
  organizationId,
}: {
  organizationId: number;
}) {
  const { data: settings, isLoading } = useNotificationSettings(organizationId);
  const updateSettings = useUpdateNotificationSettings(organizationId);
  const { showToast } = useToast();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isMasterOn = settings?.emailNotificationsEnabled ?? false;

  const saveSetting = (update: NotificationSettingsUpdate) => {
    updateSettings.mutate(update, {
      onSuccess: () => showToast('Settings saved', 'success'),
      onError: () => showToast('Failed to save settings', 'error'),
    });
  };

  return (
    <div className="space-y-4">
      <Card className="border shadow-none rounded-lg">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-medium">Email Notifications</h3>

          <SettingSwitchRow
            label="Enable Email Notifications"
            description="Send email notifications to users when new notifications are created"
            checked={isMasterOn}
            onCheckedChange={(checked) =>
              saveSetting({ emailNotificationsEnabled: checked })
            }
          />

          <div className="border-t pt-4 space-y-4">
            {NOTIFICATION_TYPE_SETTINGS.map((typeSetting) => (
              <SettingSwitchRow
                key={typeSetting.type}
                label={typeSetting.label}
                description={typeSetting.description}
                checked={settings?.[typeSetting.field] ?? false}
                disabled={!isMasterOn}
                onCheckedChange={(checked) =>
                  saveSetting({ [typeSetting.field]: checked })
                }
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <SendNotificationForm organizationId={organizationId} />
    </div>
  );
}

function SettingSwitchRow({
  label,
  description,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="data-[state=checked]:bg-green-500"
      />
    </div>
  );
}
